/**
 * My Tasks List Route (issue #1680, epic #1674)
 *
 * The current user's open Tasks — assigned to them directly or to any team
 * they belong to (see `useMyOpenTasks`). Does not surface multi-respondent
 * (`audienceType`) Tasks targeting the user's team via `TARGETS` edges —
 * `TaskFilter` (issue #1678) has no audience-scoped query today, only
 * `assigneeType`/`assigneeId`. A user who needs to respond to an
 * `EntityUsageCascade` Task today reaches it via a direct link (e.g. from a
 * notification/email sent out-of-band) rather than this list — a gap to
 * close once an audience-scoped query exists.
 */

import { useNavigate } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { useMyOpenTasks } from '../../graphql/queries/task';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import {
  DataTable,
  EmptyState,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { Task } from '../../graphql/types/task';
import './index.css';

export const Route = createFileRoute('/tasks/')({
  component: MyTasksListPage,
});

export { MyTasksListPage };

function formatDueDate(dueDate: string): string {
  return new Date(dueDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function MyTasksListPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { tasks, loading, error, refetch } = useMyOpenTasks(user?.id, user?.teams ?? []);

  const columns: DataTableColumn<Task>[] = [
    {
      key: 'name',
      label: 'Task',
      width: '35%',
      render: (task) => (
        <div>
          <div className="entity-list-name-cell">{task.name}</div>
          <div className="entity-list-description-cell">{task.taskType}</div>
        </div>
      ),
    },
    {
      key: 'subjectEntityType',
      label: 'Subject',
      width: '20%',
      render: (task) => (
        <span>
          {task.subjectEntityType} {task.subjectEntityId}
        </span>
      ),
    },
    {
      key: 'assigneeType',
      label: 'Assigned to',
      width: '15%',
      render: (task) => (
        <span>{task.assigneeType ? `${task.assigneeType} ${task.assigneeId}` : 'Audience'}</span>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due',
      width: '15%',
      render: (task) => (
        <span className={task.overdue ? 'my-tasks-list__overdue' : undefined}>
          {formatDueDate(task.dueDate)}
          {task.overdue ? ' (overdue)' : ''}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '15%',
      render: (task) => <span>{task.status}</span>,
    },
  ];

  const handleRowClick = (task: Task): void => {
    void navigate({ to: `/tasks/${task.entityId}` });
  };

  if (error) {
    return (
      <div className="my-tasks-list-page">
        <h1 className="forge-typography--headline4">My Tasks</h1>
        <EmptyState
          icon="error"
          title="Failed to load your tasks"
          description={error.message}
          actionLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div className="my-tasks-list-page">
      <div className="my-tasks-list-header">
        <div>
          <h1 className="forge-typography--headline4">My Tasks</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${tasks.length} open tasks`}
          </p>
        </div>
      </div>

      <div className="my-tasks-list-table-container">
        {loading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : (
          <DataTable
            columns={columns}
            data={tasks}
            getRowKey={(task) => task.entityId}
            onRowClick={handleRowClick}
            emptyState={{
              icon: 'checklist',
              title: 'No open tasks',
              description: "You're all caught up — nothing is waiting on you right now.",
            }}
          />
        )}
      </div>
    </div>
  );
}
